import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { Layout } from '../components/layout/Layout';
import { MATCH_TYPES, MATCH_TYPE_LABEL } from '../data/matching';
import { DECKS } from '../data/stimuli';
import { CARD_LIBRARY } from '../data/cardLibrary';
import '../styles/curriculum-manager.css';

// ── Flattened, searchable list of every image already in the app's card
// library — lets admins reuse deliberately-chosen assets instead of sourcing
// new photos for every matching trial.
const LIBRARY_ITEMS = [
  ...DECKS.flatMap(deck => deck.items.flatMap(item => [
    ...(item.image ? [{ label: item.label, image: item.image, category: deck.name }] : []),
    ...(item.variations || []).map((url, i) => ({
      label: `${item.label} (alt ${i + 1})`, image: url, category: deck.name,
    })),
  ])),
  ...CARD_LIBRARY.map(c => ({ label: c.label, image: c.image, category: c.category })),
].filter(item => item.image);

const MAX_CHOICES = 4;
const MIN_CHOICES = 2;

function StatusChip({ status }) {
  const MAP = {
    draft:     { bg: '#F3F4F6', border: '#D1D5DB', color: '#6B7280', label: 'Draft' },
    published: { bg: '#D1FAE5', border: '#6EE7B7', color: '#065F46', label: 'Published' },
    archived:  { bg: '#F3F4F6', border: '#D1D5DB', color: '#9CA3AF', label: 'Archived' },
  };
  const s = MAP[status] || MAP.draft;
  return (
    <span style={{
      padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
      background: s.bg, border: `1.5px solid ${s.border}`, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

function genId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function newChoice(isCorrect = false) {
  return { id: genId('c'), label: '', imageUrl: null, isCorrect };
}

function newTrial() {
  return {
    id: genId('t'),
    order: 0,
    prompt: { label: '', imageUrl: null, audioUrl: null },
    choices: [newChoice(true), newChoice(false), newChoice(false)],
  };
}

function compressImage(file, maxPx = 800, quality = 0.82) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };
    img.src = URL.createObjectURL(file);
  });
}

// ── Modal for picking an already-curated image out of the card library ──────

function LibraryPickerModal({ onPick, onClose }) {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const filtered = (q
    ? LIBRARY_ITEMS.filter(item => item.label.toLowerCase().includes(q) || item.category.toLowerCase().includes(q))
    : LIBRARY_ITEMS
  ).slice(0, 60);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 18, width: '100%', maxWidth: 560, maxHeight: '80vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1.5px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search the card library…"
            style={{
              flex: 1, fontSize: 14, fontFamily: 'inherit', padding: '8px 12px',
              border: '1.5px solid #E5E7EB', borderRadius: 8, outline: 'none',
            }}
          />
          <button className="cm-btn" onClick={onClose}>✕ Close</button>
        </div>
        <div style={{
          padding: 16, overflowY: 'auto',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10,
        }}>
          {filtered.length === 0 && (
            <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              No matches — try a different search.
            </p>
          )}
          {filtered.map((item, i) => (
            <button
              key={`${item.label}-${i}`}
              onClick={() => onPick(item)}
              style={{
                border: '1.5px solid #E5E7EB', borderRadius: 10, overflow: 'hidden',
                padding: 0, cursor: 'pointer', background: '#fff', textAlign: 'left',
              }}
            >
              <div style={{ width: '100%', aspectRatio: '1 / 1', background: '#F3F4F6' }}>
                <img src={item.image} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#374151', padding: '4px 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.label}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── A single image tile, reused for prompt + each choice ─────────────────────
// Supports uploading a fresh photo or picking one already in the card library.

function ImageTile({ imageUrl, label, size = 120, deckId, pathId, onPick }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const fileRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const compressed = await compressImage(file);
      const path = `matching-decks/${deckId}/${pathId}-${Date.now()}.jpg`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, compressed);
      const url = await getDownloadURL(sRef);
      onPick({ imageUrl: url });
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div
        onClick={() => fileRef.current?.click()}
        style={{
          width: size, height: size, flexShrink: 0,
          background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', position: 'relative', overflow: 'hidden', borderRadius: 12,
          border: '1.5px solid #E5E7EB',
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0])}
        />
        {uploading ? (
          <span className="gen-spinner-sm" />
        ) : imageUrl ? (
          <img src={imageUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 10, padding: 6 }}>
            <div style={{ fontSize: 20, marginBottom: 2 }}>📷</div>
            Add photo
          </div>
        )}
        {imageUrl && !uploading && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 9, fontWeight: 600,
            textAlign: 'center', padding: '3px 0',
          }}>
            Replace
          </div>
        )}
      </div>
      <button
        type="button"
        className="cm-btn"
        style={{ fontSize: 10, padding: '3px 8px', marginTop: 4, width: '100%' }}
        onClick={() => setShowLibrary(true)}
      >
        📚 Library
      </button>
      {error && <p style={{ fontSize: 11, color: '#DC2626', margin: '4px 0 0' }}>{error}</p>}
      {showLibrary && (
        <LibraryPickerModal
          onClose={() => setShowLibrary(false)}
          onPick={item => { onPick({ imageUrl: item.image, label: item.label }); setShowLibrary(false); }}
        />
      )}
    </div>
  );
}

// ── Audio tile — used for a SOUND-type trial's prompt. Admins upload a real
// sound clip (recorded or sourced) rather than a photo; no AI generation and
// no compression, since these are short audio files, not images.

function AudioTile({ audioUrl, deckId, pathId, onPick }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();
  const previewRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const ext = (file.name.split('.').pop() || 'mp3').toLowerCase();
      const path = `matching-decks/${deckId}/${pathId}-${Date.now()}.${ext}`;
      const sRef = storageRef(storage, path);
      await uploadBytes(sRef, file, { contentType: file.type || 'audio/mpeg' });
      const url = await getDownloadURL(sRef);
      onPick({ audioUrl: url });
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div
        onClick={() => fileRef.current?.click()}
        style={{
          width: 100, height: 100, flexShrink: 0,
          background: audioUrl ? '#FEF3F2' : '#F3F4F6', display: 'flex', alignItems: 'center',
          justifyContent: 'center', cursor: 'pointer', position: 'relative', overflow: 'hidden',
          borderRadius: 12, border: '1.5px solid #E5E7EB',
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0])}
        />
        {uploading ? (
          <span className="gen-spinner-sm" />
        ) : (
          <div style={{ textAlign: 'center', color: audioUrl ? '#8B2635' : '#9CA3AF', fontSize: 10, padding: 6 }}>
            <div style={{ fontSize: 24, marginBottom: 2 }}>{audioUrl ? '🔊' : '🎙️'}</div>
            {audioUrl ? 'Replace' : 'Add sound'}
          </div>
        )}
      </div>
      {audioUrl && !uploading && (
        <button
          type="button"
          className="cm-btn"
          style={{ fontSize: 10, padding: '3px 8px', marginTop: 4, width: '100%' }}
          onClick={() => previewRef.current?.play()}
        >
          ▶ Preview
        </button>
      )}
      {audioUrl && <audio ref={previewRef} src={audioUrl} style={{ display: 'none' }} />}
      {error && <p style={{ fontSize: 11, color: '#DC2626', margin: '4px 0 0' }}>{error}</p>}
    </div>
  );
}

// ── One choice within a trial (image + label + correct toggle) ──────────────

function ChoiceEditor({ choice, deckId, trialId, onChange, onDelete, canDelete }) {
  return (
    <div style={{
      display: 'flex', gap: 10, alignItems: 'flex-start', padding: 10,
      borderRadius: 12, border: `1.5px solid ${choice.isCorrect ? '#6EE7B7' : '#E5E7EB'}`,
      background: choice.isCorrect ? '#F0FDF4' : '#F9FAFB',
    }}>
      <ImageTile
        imageUrl={choice.imageUrl}
        label={choice.label}
        size={72}
        deckId={deckId}
        pathId={`${trialId}-${choice.id}`}
        onPick={({ imageUrl, label }) => onChange({
          ...choice, imageUrl, label: choice.label || label || '',
        })}
      />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <input
          value={choice.label}
          onChange={e => onChange({ ...choice, label: e.target.value })}
          placeholder="Choice label"
          style={{
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            padding: '7px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8,
            outline: 'none', color: '#111827', background: '#fff',
          }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className="cm-btn"
            style={{
              fontSize: 11,
              ...(choice.isCorrect ? { background: '#10B981', color: '#fff', borderColor: '#10B981' } : {}),
            }}
            onClick={() => onChange({ ...choice, isCorrect: true })}
            disabled={choice.isCorrect}
          >
            {choice.isCorrect ? '✓ Correct Answer' : 'Mark Correct'}
          </button>
          <button
            className="cm-btn cm-btn--danger"
            style={{ fontSize: 11 }}
            onClick={onDelete}
            disabled={!canDelete}
          >
            🗑 Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ── One trial: a prompt card + its choices ────────────────────────────────────

function TrialCard({ trial, index, total, deckId, matchType, onChange, onMoveUp, onMoveDown, onDelete }) {
  const isSound = matchType === MATCH_TYPES.SOUND;
  const updateChoice = (choiceId, updated) => {
    onChange({
      ...trial,
      choices: trial.choices.map(c => (c.id === choiceId
        ? updated
        : (updated.isCorrect ? { ...c, isCorrect: false } : c) // only one correct choice at a time
      )),
    });
  };

  const addChoice = () => {
    if (trial.choices.length >= MAX_CHOICES) return;
    onChange({ ...trial, choices: [...trial.choices, newChoice(false)] });
  };

  const removeChoice = (choiceId) => {
    if (trial.choices.length <= MIN_CHOICES) return;
    onChange({ ...trial, choices: trial.choices.filter(c => c.id !== choiceId) });
  };

  return (
    <div style={{
      background: '#fff', borderRadius: 16, border: '1.5px solid #E5E7EB',
      padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{
          width: 26, height: 26, borderRadius: '50%', background: '#8B2635', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 800, flexShrink: 0,
        }}>
          {index + 1}
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Trial {index + 1}</span>
        <div style={{ flex: 1 }} />
        <button className="cm-btn" style={{ fontSize: 11 }} onClick={onMoveUp} disabled={index === 0}>↑</button>
        <button className="cm-btn" style={{ fontSize: 11 }} onClick={onMoveDown} disabled={index === total - 1}>↓</button>
        <button className="cm-btn cm-btn--danger" style={{ fontSize: 11 }} onClick={onDelete}>🗑 Delete Trial</button>
      </div>

      {/* ── Prompt (what the child hears/sees first) ─────────────────────── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'flex-start' }}>
        {isSound ? (
          <AudioTile
            audioUrl={trial.prompt.audioUrl}
            deckId={deckId}
            pathId={`${trial.id}-prompt`}
            onPick={({ audioUrl }) => onChange({ ...trial, prompt: { ...trial.prompt, audioUrl } })}
          />
        ) : (
          <ImageTile
            imageUrl={trial.prompt.imageUrl}
            label={trial.prompt.label}
            size={100}
            deckId={deckId}
            pathId={`${trial.id}-prompt`}
            onPick={({ imageUrl, label }) => onChange({
              ...trial, prompt: { label: trial.prompt.label || label || '', imageUrl },
            })}
          />
        )}
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            {isSound ? 'Sound name (admin reference only — not shown to the child)' : 'Sample (what the child sees first)'}
          </label>
          <input
            value={trial.prompt.label}
            onChange={e => onChange({ ...trial, prompt: { ...trial.prompt, label: e.target.value } })}
            placeholder={isSound ? 'e.g., Dog barking' : 'e.g., Dog'}
            style={{
              width: '100%', boxSizing: 'border-box', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
              padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
              outline: 'none', color: '#111827', background: '#F9FAFB',
            }}
          />
          {isSound && (
            <p style={{ fontSize: 11, color: '#9CA3AF', margin: '6px 0 0' }}>
              The child only hears the sound — this label is just so you can find the trial later.
            </p>
          )}
        </div>
      </div>

      {/* ── Choices ──────────────────────────────────────────────────────── */}
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
        Choices (mark the one correct match)
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {trial.choices.map(choice => (
          <ChoiceEditor
            key={choice.id}
            choice={choice}
            deckId={deckId}
            trialId={trial.id}
            onChange={updated => updateChoice(choice.id, updated)}
            onDelete={() => removeChoice(choice.id)}
            canDelete={trial.choices.length > MIN_CHOICES}
          />
        ))}
      </div>
      {trial.choices.length < MAX_CHOICES && (
        <button className="cm-btn" style={{ fontSize: 12, marginTop: 8 }} onClick={addChoice}>
          + Add Choice
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Matching Editor — manual content workflow. No AI, no Cloud Functions.
// Admin edits basic info + trials here, then saves draft / publishes directly
// to Firestore (gated by the isAdmin() security rule).
// ══════════════════════════════════════════════════════════════════════════════

export const MatchingReviewPage = () => {
  const { deckId } = useParams();
  const navigate    = useNavigate();

  const [deck,    setDeck]    = useState(null);
  const [loading, setLoading] = useState(true);

  const [title,        setTitle]        = useState('');
  const [description,  setDescription]  = useState('');
  const [category,     setCategory]     = useState('');
  const [subcategory,  setSubcategory]  = useState('');
  const [matchType,    setMatchType]    = useState(MATCH_TYPES.IDENTICAL);
  const [tagsInput,    setTagsInput]    = useState('');
  const [trials,       setTrials]       = useState([]);

  const [saving,  setSaving]  = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const loadedOnce = useRef(false);

  useEffect(() => {
    if (!deckId) return;
    const unsub = onSnapshot(
      doc(db, 'generatedMatchingDecks', deckId),
      snap => {
        if (snap.exists()) {
          const d = { id: snap.id, ...snap.data() };
          setDeck(d);
          if (!loadedOnce.current) {
            setTitle(d.title || '');
            setDescription(d.description || '');
            setCategory(d.category || '');
            setSubcategory(d.subcategory || '');
            setMatchType(d.matchType || MATCH_TYPES.IDENTICAL);
            setTagsInput((d.tags || []).join(', '));
            setTrials((d.trials || []).slice().sort((a, b) => a.order - b.order));
            loadedOnce.current = true;
          }
        }
        setLoading(false);
      },
      err => { console.error(err); setLoading(false); }
    );
    return unsub;
  }, [deckId]);

  const renumber = (arr) => arr.map((t, i) => ({ ...t, order: i + 1 }));

  const handleAddTrial = () => {
    setTrials(prev => renumber([...prev, newTrial()]));
  };

  const handleTrialChange = (idx, updated) => {
    setTrials(prev => prev.map((t, i) => i === idx ? updated : t));
  };

  const handleMoveUp = (idx) => {
    if (idx === 0) return;
    setTrials(prev => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return renumber(next);
    });
  };

  const handleMoveDown = (idx) => {
    setTrials(prev => {
      if (idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return renumber(next);
    });
  };

  const handleDeleteTrial = (idx) => {
    setTrials(prev => renumber(prev.filter((_, i) => i !== idx)));
  };

  const buildPayload = useCallback(() => ({
    title:       title.trim(),
    description: description.trim(),
    category:    category.trim(),
    subcategory: subcategory.trim(),
    matchType,
    tags:        tagsInput.split(',').map(t => t.trim()).filter(Boolean),
    trials:      trials.map(t => ({
      id:      t.id,
      order:   t.order,
      prompt:  { label: t.prompt.label.trim(), imageUrl: t.prompt.imageUrl || null, audioUrl: t.prompt.audioUrl || null },
      choices: t.choices.map(c => ({
        id: c.id, label: c.label.trim(), imageUrl: c.imageUrl || null, isCorrect: !!c.isCorrect,
      })),
    })),
    trialCount: trials.length,
    updatedAt: serverTimestamp(),
  }), [title, description, category, subcategory, matchType, tagsInput, trials]);

  const save = async (extra, { requireValid = false } = {}) => {
    if (requireValid) {
      if (!title.trim()) return setSaveErr('Title is required to publish.');
      if (trials.length < 1) return setSaveErr('Add at least one trial before publishing.');
      for (const t of trials) {
        if (matchType === MATCH_TYPES.SOUND) {
          if (!t.prompt.audioUrl) {
            return setSaveErr('Every trial needs a sound clip.');
          }
        } else if (!t.prompt.label.trim() && !t.prompt.imageUrl) {
          return setSaveErr('Every trial needs a sample label or photo.');
        }
        if (t.choices.filter(c => c.isCorrect).length !== 1) {
          return setSaveErr('Every trial needs exactly one correct choice.');
        }
        if (t.choices.some(c => !c.label.trim() && !c.imageUrl)) {
          return setSaveErr('Every choice needs a label or a photo.');
        }
      }
    }
    setSaving(true);
    setSaveErr(null);
    try {
      await updateDoc(doc(db, 'generatedMatchingDecks', deckId), { ...buildPayload(), ...extra });
      setSavedAt(Date.now());
    } catch (err) {
      setSaveErr(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft   = () => save({ status: 'draft' });
  const handleSaveChanges = () => save({});
  const handlePublish     = () => save({ status: 'published', publishedAt: serverTimestamp() }, { requireValid: true });
  const handleUnpublish   = () => save({ status: 'draft' });
  const handleArchive     = () => save({ status: 'archived' });
  const handleRestore     = () => save({ status: 'draft' });

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '60vh', color: '#9CA3AF', fontSize: 14 }}>
          Loading…
        </div>
      </Layout>
    );
  }

  if (!deck) {
    return (
      <Layout>
        <div className="cm-page">
          <div style={{ textAlign: 'center', padding: '80px 24px', color: '#6B7280' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🔍</div>
            <h2 style={{ margin: '0 0 8px', color: '#111827' }}>Activity not found</h2>
            <button className="cm-btn cm-btn--primary" onClick={() => navigate('/admin/matching')}>
              Back to Matching Manager
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const isDraft     = deck.status === 'draft';
  const isPublished = deck.status === 'published';
  const isArchived  = deck.status === 'archived';

  return (
    <Layout>
      <div className="cm-page">

        {/* ── Top navigation ──────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          <button className="cm-btn" style={{ fontSize: 12 }} onClick={() => navigate('/admin/matching')}>
            ← Matching Manager
          </button>
          <a
            className="cm-btn"
            style={{ fontSize: 12, textDecoration: 'none' }}
            href={`/matching/generated/${deckId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            ▶ Preview as Learner
          </a>
          <div style={{ flex: 1 }} />
          <StatusChip status={deck.status} />
        </div>

        {/* ── Basic info ─────────────────────────────────────────────── */}
        <div style={{
          background: '#fff', borderRadius: 20, border: '1.5px solid #E5E7EB',
          padding: '24px 28px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <label className="cm-subtitle" style={{ display: 'block', fontSize: 11, fontWeight: 700,
            color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Title
          </label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Activity title"
            style={{
              width: '100%', boxSizing: 'border-box', fontSize: 22, fontWeight: 800,
              fontFamily: 'inherit', padding: '6px 0', border: 'none', borderBottom: '2px solid #E5E7EB',
              outline: 'none', color: '#111827', marginBottom: 16, background: 'transparent',
            }}
          />

          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Description
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
            placeholder="Brief description (optional)"
            style={{
              width: '100%', boxSizing: 'border-box', resize: 'vertical', fontSize: 13,
              fontFamily: 'inherit', padding: '8px 12px', border: '1.5px solid #E5E7EB',
              borderRadius: 8, outline: 'none', color: '#374151', marginBottom: 16, lineHeight: 1.5,
            }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Category</label>
              <input
                value={category}
                onChange={e => setCategory(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box', fontSize: 13, fontFamily: 'inherit',
                  padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
                  outline: 'none', color: '#374151',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Subcategory</label>
              <input
                value={subcategory}
                onChange={e => setSubcategory(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box', fontSize: 13, fontFamily: 'inherit',
                  padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
                  outline: 'none', color: '#374151',
                }}
              />
            </div>
          </div>

          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Match Type</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {Object.values(MATCH_TYPES).map(type => (
              <button
                key={type}
                onClick={() => setMatchType(type)}
                style={{
                  padding: '7px 14px', borderRadius: 20, border: '1.5px solid',
                  borderColor: matchType === type ? '#8B2635' : '#E5E7EB',
                  background:  matchType === type ? '#8B263518' : '#F9FAFB',
                  color:       matchType === type ? '#8B2635' : '#6B7280',
                  fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                }}
              >
                {MATCH_TYPE_LABEL[type]}
              </button>
            ))}
          </div>

          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Tags</label>
          <input
            value={tagsInput}
            onChange={e => setTagsInput(e.target.value)}
            placeholder="comma, separated, tags"
            style={{
              width: '100%', boxSizing: 'border-box', fontSize: 13, fontFamily: 'inherit',
              padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8,
              outline: 'none', color: '#374151',
            }}
          />
        </div>

        {/* ── Status actions ─────────────────────────────────────────── */}
        <div style={{
          background: isPublished ? '#F0FDF4' : isArchived ? '#F9FAFB' : '#FFFBEB',
          border: `1.5px solid ${isPublished ? '#86EFAC' : isArchived ? '#E5E7EB' : '#FCD34D'}`,
          borderRadius: 14, padding: '16px 20px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
              {isPublished ? '✓ Published — visible to customers' : isArchived ? 'Archived — hidden from everyone' : 'Draft — not visible to customers yet'}
            </div>
            {savedAt && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Saved ✓</div>}
            {saveErr && <div style={{ fontSize: 12, color: '#DC2626', marginTop: 2 }}>{saveErr}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isDraft && (
              <>
                <button className="cm-btn" disabled={saving} onClick={handleSaveDraft}>
                  {saving ? 'Saving…' : '💾 Save Draft'}
                </button>
                <button className="cm-btn cm-btn--primary" disabled={saving} onClick={handlePublish}>
                  {saving ? 'Publishing…' : '▶ Publish'}
                </button>
              </>
            )}
            {isPublished && (
              <>
                <button className="cm-btn" disabled={saving} onClick={handleSaveChanges}>
                  {saving ? 'Saving…' : '💾 Save Changes'}
                </button>
                <button className="cm-btn" disabled={saving} onClick={handleUnpublish}>
                  Unpublish
                </button>
                <button className="cm-btn cm-btn--danger" disabled={saving} onClick={handleArchive}>
                  Archive
                </button>
              </>
            )}
            {isArchived && (
              <button className="cm-btn cm-btn--primary" disabled={saving} onClick={handleRestore}>
                Restore to Draft
              </button>
            )}
          </div>
        </div>

        {/* ── Trials ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ fontSize: 14, fontWeight: 800, color: '#374151', textTransform: 'uppercase',
            letterSpacing: '0.06em', margin: 0 }}>
            Trials ({trials.length})
          </h2>
          <button className="cm-btn cm-btn--primary" style={{ fontSize: 12 }} onClick={handleAddTrial}>
            + Add Trial
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
          {trials.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF', fontSize: 13 }}>
              No trials yet — click "+ Add Trial" to begin.
            </div>
          )}
          {trials.map((trial, idx) => (
            <TrialCard
              key={trial.id}
              trial={trial}
              index={idx}
              total={trials.length}
              deckId={deckId}
              matchType={matchType}
              onChange={updated => handleTrialChange(idx, updated)}
              onMoveUp={() => handleMoveUp(idx)}
              onMoveDown={() => handleMoveDown(idx)}
              onDelete={() => handleDeleteTrial(idx)}
            />
          ))}
        </div>

        {/* ── Bottom save bar ────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          {saveErr && <span style={{ fontSize: 12, color: '#DC2626', alignSelf: 'center' }}>{saveErr}</span>}
          {isDraft && (
            <>
              <button className="cm-btn" disabled={saving} onClick={handleSaveDraft}>
                {saving ? 'Saving…' : '💾 Save Draft'}
              </button>
              <button className="cm-btn cm-btn--primary" disabled={saving} onClick={handlePublish}>
                {saving ? 'Publishing…' : '▶ Publish'}
              </button>
            </>
          )}
          {isPublished && (
            <button className="cm-btn cm-btn--primary" disabled={saving} onClick={handleSaveChanges}>
              {saving ? 'Saving…' : '💾 Save Changes'}
            </button>
          )}
          {isArchived && (
            <button className="cm-btn cm-btn--primary" disabled={saving} onClick={handleRestore}>
              Restore to Draft
            </button>
          )}
        </div>

      </div>
    </Layout>
  );
};
